import { doc, getDoc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase-config';
import { geminiGenerateJson } from '../../geminiService';
import { AI_PIPELINE_SCHEMA_V2 } from '../../../constants/aiPipelineModes';
import { normalizeStationAnalysisVerifications } from '../../../utils/normalizeVerification';
import { computeOverallMaturityScore } from '../../../utils/overallMaturityScore';
import { seedSchoolSchemaFromRegistration } from './seedSchoolSchemaFromRegistration';
import { schoolSchemaRef } from './schoolSchemaPaths';
import { getEvidenceAnalysisJob } from './evidenceAnalysisJob';
import { ensureSoftEvidenceAndClaims } from './softEvidenceAndClaims';
import {
  buildDraftContextUnits,
  packUnitsIntoSizeChunks,
  chunkPayloads,
  clipText,
} from './draftContextPacker';

/** Keep under functions/geminiClient MAX_PROMPT_CHARS (120000). */
const PROMPT_BUDGET = 95000;
/** Variable payload budget per map chunk (core context + instructions sit outside this). */
const CHUNK_UNIT_BUDGET = 45000;
/** Cap map findings fed into the reduce prompt. */
const REDUCE_FINDINGS_BUDGET = 55000;

function clipJson(value, maxChars) {
  const raw = JSON.stringify(value);
  if (raw.length <= maxChars) return raw;
  return `${raw.slice(0, Math.max(0, maxChars - 20))}…[truncated]"`;
}

function plainSchemaForPrompt(fields, valueMax = 280) {
  const out = {};
  Object.entries(fields || {}).forEach(([id, e]) => {
    const label = id
      .replace(/^meta\./, 'School: ')
      .replace(/^demo\./, 'Pupils / demographics: ')
      .replace(/^staff\./, 'Staffing: ')
      .replace(/^spec\./, 'Specialist provision: ')
      .replace(/^impact\./, 'Impact: ')
      .replace(/^policy\./, 'Policy: ')
      .replace(/_/g, ' ');
    let value = e?.value;
    if (typeof value === 'string') value = clipText(value, valueMax);
    else if (value != null && typeof value === 'object') {
      const j = JSON.stringify(value);
      value = j.length <= valueMax ? value : clipText(j, valueMax);
    }
    out[label] = {
      value,
      from: e?.provenance?.source || 'unknown',
      file: e?.provenance?.fileName || null,
      confidence: e?.confidence,
    };
  });
  return out;
}

function compactContactLog(log, maxItems = 20, noteMax = 400) {
  return (Array.isArray(log) ? log : []).slice(-maxItems).map((row) => {
    if (typeof row === 'string') return clipText(row, noteMax);
    if (row && typeof row === 'object') {
      return {
        ...row,
        note: clipText(row.note || row.text || row.body || '', noteMax),
        text: undefined,
        body: undefined,
      };
    }
    return row;
  });
}

function qualityTotals(files) {
  const list = Array.isArray(files) ? files : [];
  return {
    files: list.length,
    extracted: list.filter((f) => f.status === 'extracted' || f.status === 'done').length,
    failed: list.filter((f) => f.status === 'failed').length,
    skippedPhoto: list.filter((f) => f.status === 'skipped_photo_policy').length,
    skippedVideo: list.filter((f) => f.status === 'skipped_video').length,
    skippedDuplicate: list.filter((f) => f.status === 'skipped_duplicate').length,
  };
}

function buildCoreContext({
  schoolName,
  targetLang,
  level,
  schemaPlain,
  contactLog,
  precis,
  metrics,
  inventory,
  jobNote,
  qualityTotals: totals,
}) {
  return {
    schoolName,
    targetLang,
    level,
    schemaPlain,
    contactLog,
    precis: clipText(precis || '', 3500),
    metrics,
    inventory: clipText(inventory, 3500),
    jobNote,
    qualityTotals: totals,
  };
}

const REPORT_JSON_SHAPE = `{
  "institutionalBrief": { "executiveSummary": "250-350 word public pen portrait" },
  "stationAnalysis": {
    "01": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "02": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "03": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "04": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "05": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "06": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } },
    "07": { "ifipRating": 1, "content": "", "verification": { "anchors": ["plain string"], "gaps": ["plain string"] } }
  },
  "evidencePortfolio": {
    "registry": [ { "fileName": "", "category": "", "evidenceSummary": "", "proves": "", "strength": 1 } ],
    "gaps": ""
  },
  "strategicRoadmap": { "content": "", "nextPhase": "", "resources": "" },
  "internalNotes": "Plain English accreditor briefing only"
}`;

function buildSingleShotPrompt(core, unitsPayload) {
  return `
SYSTEM: You are the IFIP Lead Accreditation Auditor (schema_v2).
Generate a formal report for ${core.schoolName}.
PIPELINE: ${AI_PIPELINE_SCHEMA_V2}
MANDATORY: Entire report in ${core.targetLang}. Return ONLY one JSON object (no commentary after it).

CORE (always trust first):
- SCHEMA: ${clipJson(core.schemaPlain, 22000)}
- ACCREDITOR CONTACT LOG: ${clipJson(core.contactLog, 5000)}
- ACCREDITOR PRECIS: ${clipJson(core.precis, 4000)}
- CLAIM METRICS: ${clipJson(core.metrics, 2000)}
- EVIDENCE INVENTORY: ${core.inventory}
- JOB: ${core.jobNote}
- FILE QUALITY TOTALS: ${clipJson(core.qualityTotals, 800)}
- LEVEL: ${core.level}

VARIABLE CONTEXT UNITS (size-packed; may be SEF stations, claims, soft evidence, file quality, registration answers):
${clipJson(unitsPayload, 48000)}

RULES:
- Prefer schema + accreditor notes over SEF for facts.
- Soft evidence is tone-only unless file/schema backed.
- Prefer accreditor over school on conflict; record DISCONNECT lines in internalNotes.
- Do not invent unread file contents.
- verification.anchors and verification.gaps MUST be arrays of short plain strings only.
- Plain English internal notes; never raw schema keys.
- TONE: Professional, clinical. No em dashes or bold.

OUTPUT: Return ONLY raw JSON:
${REPORT_JSON_SHAPE}
`.trim();
}

function buildMapPrompt(core, chunkIndex, chunkCount, unitsPayload) {
  return `
SYSTEM: IFIP schema_v2 draft MAP pass (chunk ${chunkIndex + 1} of ${chunkCount}).
School: ${core.schoolName}. Language: ${core.targetLang}. Level: ${core.level}.
Return ONLY one JSON object (no commentary after it).

CORE (for triangulation; do not rewrite full report yet):
- SCHEMA: ${clipJson(core.schemaPlain, 16000)}
- ACCREDITOR NOTES: ${clipJson(core.precis, 2500)}
- CLAIM METRICS: ${clipJson(core.metrics, 1500)}
- JOB: ${core.jobNote}

CONTEXT UNITS IN THIS CHUNK:
${clipJson(unitsPayload, 50000)}

Extract findings only from these units. Do not invent files you have not seen.

OUTPUT JSON:
{
  "chunkIndex": ${chunkIndex},
  "stationFindings": {
    "01": { "suggestedRating": 0, "contentHints": "", "anchors": ["plain string"], "gaps": ["plain string"] }
  },
  "portfolioHints": [ { "fileName": "", "category": "", "evidenceSummary": "", "proves": "", "strength": 1 } ],
  "disconnects": ["plain English"],
  "internalNoteBullets": ["plain English"],
  "portraitHints": ["short factual points for public portrait"]
}
`.trim();
}

function buildReducePrompt(core, mapResults) {
  return `
SYSTEM: IFIP schema_v2 draft REDUCE pass.
Synthesise the final formal report for ${core.schoolName}.
PIPELINE: ${AI_PIPELINE_SCHEMA_V2}
MANDATORY: Entire report in ${core.targetLang}. Return ONLY one JSON object (no commentary after it).

CORE:
- SCHEMA: ${clipJson(core.schemaPlain, 20000)}
- ACCREDITOR CONTACT LOG: ${clipJson(core.contactLog, 5000)}
- ACCREDITOR PRECIS: ${clipJson(core.precis, 3500)}
- CLAIM METRICS: ${clipJson(core.metrics, 2000)}
- INVENTORY: ${core.inventory}
- JOB: ${core.jobNote}
- FILE QUALITY TOTALS: ${clipJson(core.qualityTotals, 800)}
- LEVEL: ${core.level}

MAP FINDINGS FROM SIZE-PACKED CHUNKS:
${clipJson(mapResults, REDUCE_FINDINGS_BUDGET)}

Merge station findings (prefer stronger evidence / higher confidence). Deduplicate portfolio and notes.
Same rules as single-shot: schema + accreditor over SEF; plain-English internalNotes; string-only anchors/gaps.

OUTPUT: Return ONLY raw JSON:
${REPORT_JSON_SHAPE}
`.trim();
}

/**
 * Schema v2 report draft (additive).
 * Size-budgeted context packing: fill chunks to a char budget, map each chunk, then reduce.
 * Small schools may still complete in a single shot when one packed chunk fits.
 */
export async function generateReportDraftSchemaV2({ schoolId, user, targetLang = 'English', deps }) {
  const { migrateReportStaffWorkspaceFromLegacyIfNeeded, logAction, reportStaffWorkspaceRef } = deps;
  if (typeof reportStaffWorkspaceRef !== 'function') {
    throw new Error('reportStaffWorkspaceRef required in deps');
  }

  await migrateReportStaffWorkspaceFromLegacyIfNeeded(schoolId);
  await seedSchoolSchemaFromRegistration(schoolId);
  const claimsBundle = await ensureSoftEvidenceAndClaims(schoolId);

  const schoolRef = doc(db, 'schools', schoolId);
  const auditRef = doc(db, 'audits', schoolId);
  const reportRef = doc(db, 'reports', schoolId);
  const wsRef = reportStaffWorkspaceRef(schoolId);

  const [schoolSnap, auditSnap, reportSnap, schemaSnap, wsSnap, job] = await Promise.all([
    getDoc(schoolRef),
    getDoc(auditRef),
    getDoc(reportRef),
    getDoc(schoolSchemaRef(schoolId)),
    getDoc(wsRef),
    getEvidenceAnalysisJob(schoolId),
  ]);

  const schoolRaw = schoolSnap.data() || {};
  const schoolData = schoolRaw.answers || {};
  const auditData = auditSnap.data() || { stations: {}, evidenceVault: [] };
  const fullReportData = reportSnap.data() || {};
  const schemaData = schemaSnap.exists() ? schemaSnap.data() : { fields: {} };
  const wsData = wsSnap.exists() ? wsSnap.data() : {};
  const contactLogForPrompt =
    Array.isArray(wsData.accreditorContactLog) && wsData.accreditorContactLog.length
      ? wsData.accreditorContactLog
      : fullReportData?.meta?.accreditorContactLog || [];
  const internalPrecisForPrompt =
    typeof wsData.internalNotes === 'string' && wsData.internalNotes.trim() !== ''
      ? wsData.internalNotes
      : typeof fullReportData.internalNotes === 'string'
        ? fullReportData.internalNotes
        : '';

  if (!fullReportData.meta?.authorUid) {
    await updateDoc(reportRef, { 'meta.authorUid': user.uid });
  }

  const softEvidence =
    Array.isArray(schemaData.softEvidence) && schemaData.softEvidence.length
      ? schemaData.softEvidence
      : claimsBundle.softEvidence || [];
  const claimsMap =
    Array.isArray(schemaData.claimsMap) && schemaData.claimsMap.length
      ? schemaData.claimsMap
      : claimsBundle.claimsMap || [];
  const claimMetrics = schemaData.claimMetrics || claimsBundle.claimMetrics || {};

  const jobNote =
    job?.queued > 0
      ? `Evidence job: ${job.done || 0}/${job.total || 0} finished; ${job.queued} still queued. Do not invent unread file contents.`
      : job
        ? `Evidence job status: ${job.status}. Skipped: ${job.skipped || 0}. Failed: ${job.failed || 0}.`
        : 'No evidence analysis job yet.';

  const inventory = (auditData.evidenceVault || []).map((f) => f.name).join(', ') || '(none)';
  const level = String(schoolRaw?.accreditationLevel || 'foundation').toLowerCase();

  const core = buildCoreContext({
    schoolName: schoolRaw?.officialName,
    targetLang,
    level,
    schemaPlain: plainSchemaForPrompt(schemaData.fields),
    contactLog: compactContactLog(contactLogForPrompt),
    precis: internalPrecisForPrompt,
    metrics: claimMetrics,
    inventory,
    jobNote,
    qualityTotals: qualityTotals(job?.files || []),
  });

  const units = buildDraftContextUnits({
    stations: auditData.stations,
    claims: claimsMap,
    softEvidence,
    files: job?.files || [],
    answers: schoolData,
  });
  const packed = packUnitsIntoSizeChunks(units, CHUNK_UNIT_BUDGET);
  const chunks = packed.chunks.length ? packed.chunks : [{ index: 0, units: [], size: 2 }];

  let reportContent;
  let draftStrategy = 'single_shot';

  if (chunks.length === 1) {
    const unitsPayload = chunkPayloads(chunks[0]);
    const prompt = buildSingleShotPrompt(core, unitsPayload);
    if (prompt.length > PROMPT_BUDGET) {
      // Fall through to map/reduce with smaller effective unit budget.
      draftStrategy = 'map_reduce_oversized_single';
    } else {
      reportContent = await geminiGenerateJson(prompt);
    }
  }

  if (!reportContent) {
    draftStrategy = chunks.length === 1 ? 'map_reduce_oversized_single' : 'map_reduce';
    const mapResults = [];
    for (let i = 0; i < chunks.length; i++) {
      const unitsPayload = chunkPayloads(chunks[i]);
      const mapPrompt = buildMapPrompt(core, i, chunks.length, unitsPayload);
      if (mapPrompt.length > PROMPT_BUDGET) {
        throw new Error(
          `Draft map chunk ${i + 1} still exceeds prompt budget (${mapPrompt.length} chars).`
        );
      }
      const partial = await geminiGenerateJson(mapPrompt);
      mapResults.push(partial);
    }
    const reducePrompt = buildReducePrompt(core, mapResults);
    if (reducePrompt.length > PROMPT_BUDGET) {
      // Drop lower-value map fields if reduce is still too large.
      const slim = mapResults.map((r) => ({
        chunkIndex: r.chunkIndex,
        stationFindings: r.stationFindings,
        disconnects: Array.isArray(r.disconnects) ? r.disconnects.slice(0, 12) : [],
        internalNoteBullets: Array.isArray(r.internalNoteBullets)
          ? r.internalNoteBullets.slice(0, 20)
          : [],
        portraitHints: Array.isArray(r.portraitHints) ? r.portraitHints.slice(0, 12) : [],
        portfolioHints: Array.isArray(r.portfolioHints) ? r.portfolioHints.slice(0, 15) : [],
      }));
      const retry = buildReducePrompt(core, slim);
      if (retry.length > PROMPT_BUDGET) {
        throw new Error(
          `Draft reduce pass still exceeds prompt budget (${retry.length} chars).`
        );
      }
      reportContent = await geminiGenerateJson(retry);
    } else {
      reportContent = await geminiGenerateJson(reducePrompt);
    }
  }

  if (!reportContent.evidencePortfolio || typeof reportContent.evidencePortfolio !== 'object') {
    reportContent.evidencePortfolio = { registry: [], gaps: '' };
  }
  if (!Array.isArray(reportContent.evidencePortfolio.registry)) {
    reportContent.evidencePortfolio.registry = [];
  }
  normalizeStationAnalysisVerifications(reportContent.stationAnalysis);

  const avgScore = computeOverallMaturityScore(reportContent.stationAnalysis);

  const aiInternalPrecis = reportContent.internalNotes;
  delete reportContent.internalNotes;

  await updateDoc(reportRef, {
    ...reportContent,
    'meta.isGenerated': true,
    'meta.generatedAt': serverTimestamp(),
    'meta.overallMaturityScore': avgScore,
    'meta.aiPipeline': AI_PIPELINE_SCHEMA_V2,
    'meta.v2ClaimMetrics': claimMetrics,
    'meta.v2DraftStrategy': draftStrategy,
    'meta.v2DraftChunks': chunks.length,
    'meta.v2DraftUnits': packed.totalUnits,
    workflowStep: 'ai_draft_ready',
  });

  if (aiInternalPrecis !== undefined && aiInternalPrecis !== null) {
    await setDoc(
      wsRef,
      { internalNotes: String(aiInternalPrecis), updatedAt: serverTimestamp() },
      { merge: true }
    );
  }

  await logAction('AI/System', 'REPORT_GENERATION_COMPLETE', {
    schoolId,
    pipeline: AI_PIPELINE_SCHEMA_V2,
    draftStrategy,
    chunks: chunks.length,
    units: packed.totalUnits,
  });
}
